// src/screens/manger/collections/CollectionManager.tsx
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  useColorModeValue,
  Flex,
  VStack,
  Heading,
  Text,
  HStack,
  Button,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogBody,
  AlertDialogFooter,
  useToast,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { format } from "date-fns";
import { Collection } from "../../../entity/Collection";
import { CreateUpdateCollection } from "./components/CreateUpdateCollection";
import { Method } from "../../../services/configuration/Configuration";
import { WSCollectionInstance } from "../../../services/remote/delivery/WSCollection";
import { useSelector } from "react-redux";
import { selectDeliveryCompanies } from "../../../store/selectors/DeliverySelector";
import { useParams } from "react-router-dom";

export const CollectionManager: React.FC = () => {
  const { t } = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollection, setSelectedCollection] =
    useState<Collection | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] =
    useState<Collection | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const { companyId } = useParams();

  const fetchCollections = async () => {
    try {
      const response = await WSCollectionInstance.getCollections(
        companyId || "",
      );
      setCollections(response);
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  const handleSave = async (collection: Collection) => {
    try {
      if (collection.id) {
        setCollections((prev) =>
          prev.map((c) =>
            c.id === collection.id
              ? { ...collection, updatedAt: new Date() }
              : c,
          ),
        );
        toast({
          title: t("collection.updated") || "Collection updated",
          description:
            t("collection.updatedMessage") ||
            "Collection has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new collection
        const newCollection = {
          ...collection,
          id: Date.now().toString(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setCollections((prev) => [...prev, newCollection]);
        await WSCollectionInstance.createCollections(newCollection);
        toast({
          title: t("collection.created") || "Collection created",
          description:
            t("collection.createdMessage") ||
            "Collection has been created successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      setIsDrawerOpen(false);
      setSelectedCollection(null);
    } catch (error) {
      toast({
        title: t("collection.saveFailed") || "Save failed",
        description:
          t("collection.saveFailedMessage") || "Failed to save collection.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = (collection: Collection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (collectionToDelete?.id) {
      try {
        setCollections((prev) =>
          prev.filter((c) => c.id !== collectionToDelete.id),
        );
        toast({
          title: t("collection.deleted") || "Collection deleted",
          description:
            t("collection.deletedMessage") ||
            "Collection has been deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: t("collection.deleteFailed") || "Delete failed",
          description:
            t("collection.deleteFailedMessage") ||
            "Failed to delete collection.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setDeleteDialogOpen(false);
        setCollectionToDelete(null);
      }
    }
  };

  const handleEdit = (collection: Collection) => {
    setSelectedCollection(collection);
    setIsDrawerOpen(true);
  };

  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    fetchCollections();
  }, [companyId]);

  return (
    <Box p={6}>
      <HStack justify="space-between" align="center" mb={8}>
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="semibold">
            {t("collection.title") || "Collections"}
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {t("collection.subtitle") || "Manage your data collections"}
          </Text>
        </VStack>
        <Button
          leftIcon={<AddIcon />}
          onClick={() => setIsDrawerOpen(true)}
          colorScheme="blue"
        >
          {t("collection.addNew") || "Add New Collection"}
        </Button>
      </HStack>

      <Box
        bg={tableBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        shadow="md"
      >
        <Table variant="simple">
          <Thead bg={headerBg}>
            <Tr>
              <Th>{t("collection.name") || "Name"}</Th>
              <Th>{t("type") || "Type"}</Th>
              <Th>{t("collection.fields") || "Fields"}</Th>
              <Th>{t("collection.status") || "Status"}</Th>
              <Th>{t("collection.created") || "Created"}</Th>
              <Th>{t("collection.updated") || "Updated"}</Th>
              <Th>{t("actions") || "Actions"}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {collections.map((collection) => (
              <Tr
                key={collection.id}
                _hover={{ bg: "gray.50", _dark: "gray.700" }}
                transition="background-color 0.2s"
              >
                <Td>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" fontSize="md">
                      {collection.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {collection.description}
                    </Text>
                  </VStack>
                </Td>

                <Td>
                  <Badge
                    colorScheme={
                      collection.type === Method.POST.toString()
                        ? "blue"
                        : collection.type === Method.GET.toString()
                          ? "green"
                          : collection.type === Method.PUT.toString()
                            ? "purple"
                            : "orange"
                    }
                    variant="solid"
                    textTransform="capitalize"
                  >
                    {collection.type}
                  </Badge>
                </Td>

                <Td>
                  <Text fontSize="sm">
                    {collection.fields.length}{" "}
                    {t("collection.fields") || "fields"}
                  </Text>
                </Td>

                <Td>
                  <Badge
                    colorScheme={collection.isActive ? "green" : "red"}
                    variant="solid"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {collection.isActive
                      ? t("collection.active") || "Active"
                      : t("collection.inactive") || "Inactive"}
                  </Badge>
                </Td>

                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {format(new Date(collection.createdAt!!), "MMM dd, yyyy")}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {format(new Date(collection.createdAt!!), "HH:mm")}
                  </Text>
                </Td>

                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {format(new Date(collection.updatedAt!!), "MMM dd, yyyy")}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {format(new Date(collection.updatedAt!!), "HH:mm")}
                  </Text>
                </Td>

                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Edit collection"
                      icon={<EditIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => handleEdit(collection)}
                    />
                    <IconButton
                      aria-label="Delete collection"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => handleDelete(collection)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {collections.length === 0 && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={16}
            bg="gray.50"
          >
            <Text fontSize="lg" fontWeight="semibold" color="gray.700" mb={2}>
              {t("collection.noCollections") || "No collections found"}
            </Text>
            <Text color="gray.500" textAlign="center">
              {t("collection.noCollectionsMessage") ||
                "Create your first collection to get started."}
            </Text>
          </Flex>
        )}
      </Box>

      <CreateUpdateCollection
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedCollection(null);
        }}
        collection={selectedCollection}
        onSave={handleSave}
      />

      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("collection.deleteTitle") || "Delete Collection"}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t("collection.deleteConfirmation") ||
                "Are you sure you want to delete this collection?"}
              <br />
              <strong>{collectionToDelete?.name}</strong>
              <br />
              {t("collection.deleteWarning") || "This action cannot be undone."}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setDeleteDialogOpen(false)}
              >
                {t("cancel") || "Cancel"}
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                {t("delete") || "Delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
